import { useEffect, useRef, useState } from "react";

import { LANGUAGE_CONFIG } from "../../../../config";
import { translateSmsText } from "../../../../services/api/core";

/** @type {Map<string, string>} `${target}\\0${source}` to translated body with notice. */
const translationCache = new Map();
/** @type {Map<string, Promise<string>>} */
const translationInflight = new Map();

/**
 * Cache key so each target language keeps its own translation of a body.
 *
 * @param {string} targetLanguage Preferred-language code.
 * @param {string} sourceText Official SMS body.
 * @returns {string} Cache key.
 */
function cacheKey(targetLanguage, sourceText) {
  return `${targetLanguage}\0${sourceText}`;
}

/**
 * Translate one finished default-language SMS and append that language's notice.
 * Successful results are cached; failures are not, so a later preview can retry.
 *
 * @param {string} sourceText Finished SMS body in the default language.
 * @param {string} targetLanguage Preferred-language code to translate into.
 * @returns {Promise<string>} Translated body, or "" when translation fails.
 */
function loadTranslatedSms(sourceText, targetLanguage) {
  const key = cacheKey(targetLanguage, sourceText);
  if (translationCache.has(key)) {
    return Promise.resolve(translationCache.get(key));
  }
  if (translationInflight.has(key)) {
    return translationInflight.get(key);
  }

  const request = translateSmsText(sourceText, targetLanguage)
    .then((result) => {
      const translated = (result?.translatedText || "").trim();
      if (!result?.success || !translated) {
        return "";
      }
      translationCache.set(key, translated);
      return translated;
    })
    .catch(() => "")
    .finally(() => {
      translationInflight.delete(key);
    });

  translationInflight.set(key, request);
  return request;
}

/**
 * Translation previews for a finished default-language SMS.
 * Does not call translate when `enabled` is false, the text is blank, or no targets are set.
 *
 * @param {string} sourceText Personalized message currently on screen.
 * @param {Object} [options]
 * @param {boolean} [options.enabled=false] Whether a non-default recipient is being previewed.
 * @param {string[]} [options.targetLanguages=[]] Preferred-language codes to preview.
 * @param {number} [options.debounceMs=0] Delay before the translate calls. Use for live typing.
 * @returns {string[]} Translated bodies with notices. Empty while disabled, pending, or failed.
 */
export function useSmsTranslationPreviews(
  sourceText,
  { enabled = false, targetLanguages = [], debounceMs = 0 } = {}
) {
  const source = enabled ? String(sourceText || "").trim() : "";
  const targetsKey = (targetLanguages || [])
    .map((code) => String(code || "").trim().toLowerCase())
    .filter((code) => code && code !== LANGUAGE_CONFIG.DEFAULT)
    .join("\0");
  const [previews, setPreviews] = useState([]);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!source || !targetsKey) {
      requestIdRef.current += 1;
      setPreviews([]);
      return undefined;
    }

    const targets = targetsKey.split("\0");
    if (targets.every((target) => translationCache.has(cacheKey(target, source)))) {
      requestIdRef.current += 1;
      setPreviews(
        targets
          .map((target) => translationCache.get(cacheKey(target, source)))
          .filter(Boolean)
      );
      return undefined;
    }

    setPreviews([]);
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      Promise.all(targets.map((target) => loadTranslatedSms(source, target))).then(
        (bodies) => {
          if (requestId !== requestIdRef.current) {
            return;
          }
          setPreviews(bodies.filter(Boolean));
        }
      );
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [source, targetsKey, debounceMs]);

  return source && targetsKey ? previews : [];
}
